import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { FiltersMessages, IndexJobDTO, RegularFilterConfig } from '../../models'
import { getCurrentInstance } from 'vue'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { FiltersUtils } from '../../utils/filters.utils'
import { mapGetters } from '../utils'

export interface IndexJobPivotsComposable {
  getPivots: (job: IndexJobDTO) => Record<string, Array<string>>
  getPivotLabel: (job: IndexJobDTO, pivotKey: string) => string
  getPivotValueLabel: (job: IndexJobDTO, pivotKey: string, pivotValue: string) => string
}

export function useIndexJobPivots(): IndexJobPivotsComposable {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const { instances } = mapGetters<Array<Instance>>(ctx.proxy.$store, CONFIG_CONSTS.moduleName, {
    instances: CONFIG_CONSTS.getters.instances,
  })

  const filtersMessages = ctx.proxy.$epb.messages.filters as FiltersMessages

  function getPivots(job: IndexJobDTO): Record<string, Array<string>> {
    return job.deployments.reduce<Record<string, Array<string>>>((result, deployment) => {
      Object.entries(deployment.pivots).forEach(([pivotKey, pivotValue]) => {
        if (!result.hasOwnProperty(pivotKey)) {
          result[pivotKey] = []
        }
        if (!result[pivotKey].includes(pivotValue)) {
          result[pivotKey].push(pivotValue)
        }
      })
      return result
    }, {})
  }

  function getPivotLabel(job: IndexJobDTO, pivotKey: string): string {
    let result = pivotKey
    const pivotConfig = getPivotConfig(job, pivotKey)
    if (pivotConfig) {
      result = FiltersUtils.getLabel(pivotConfig, filtersMessages)
    }
    return result
  }

  function getPivotConfig(job: IndexJobDTO, pivotKey: string): RegularFilterConfig | undefined {
    let result: RegularFilterConfig | undefined
    const instance = getInstance(job)
    if (instance) {
      result = FiltersUtils.findConfig(Object.values(instance.filters), {
        toolingId: pivotKey,
      }) as RegularFilterConfig
    }
    return result
  }

  function getInstance(job: IndexJobDTO): Instance | undefined {
    return instances.value.find(instance => instance.id === job.clientId)
  }

  function getPivotValueLabel(job: IndexJobDTO, pivotKey: string, pivotValue: string): string {
    let result = pivotValue
    const pivotConfig = getPivotConfig(job, pivotKey)
    if (pivotConfig) {
      const pivotValueConfig = FiltersUtils.findValueConfig(pivotConfig.values, {
        taggingId: pivotValue,
      })
      if (pivotValueConfig) {
        result = FiltersUtils.getValueLabel(pivotConfig, pivotValueConfig, filtersMessages)
      }
    }
    return result
  }

  return {
    getPivots,
    getPivotLabel,
    getPivotValueLabel,
  }
}
